import React, { useState } from 'react'
import Menu, { MenuProps } from 'components/menu'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useApplicationStore } from 'hooks/application'
import { MenuIconSpinner, MenuItem, MenuLabel, MenuList } from 'components/menu/items'
import api from 'helpers/api'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import { Csv, Excel, Zip } from 'components/icons'
import {
  buildExportRequestBody,
  ExportFormat,
  exportAttachmentsAsZip,
  hasExportableAttachments,
  exportFilename
} from 'components/spreadsheet/helpers/export'
import { IDownloadZipProgress } from 'components/spreadsheet/types'
import { useSlicers } from 'providers/slicers'

// The view's export choices, as a plain menu. It stays open while a download is prepared, with a
// spinner in the chosen row's icon, and closes once the file is on its way.
const ExportMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData } = useDataContext()
  const { selections: slicerSelections } = useSlicers()
  const { displayErrorMessage } = useApplicationStore()
  const [downloadingFormat, setDownloadingFormat] = useState<ExportFormat | null>(null)
  const [downloadingZip, setDownloadingZip] = useState<boolean>(false)
  const [zipProgress, setZipProgress] = useState<IDownloadZipProgress>({ done: 0, total: 0 })

  const hasAttachmentColumns = spreadsheetData?.viewDetails.columns.some((c) => c.kind === 'attachment')
  // The same files the ZIP would take, so the row is only on when there's something to download.
  const canDownloadAttachments = hasAttachmentColumns && hasExportableAttachments(spreadsheetData)

  const close = () => setMenuState({ ...menuState, open: false })
  // One download at a time: a second would be hidden when the first closes the menu.
  const busy = downloadingFormat !== null || downloadingZip

  // Exports what the user sees now — live filters, slicers, sort, hidden columns and (for
  // Excel) colour rules — whether or not the view has been saved.
  const handleExport = async (format: ExportFormat) => {
    if (busy || !spreadsheetData) return
    setDownloadingFormat(format)
    const ready = await api
      .exportTableView(
        spreadsheetData.viewDetails.publicId,
        exportFilename(spreadsheetData, format),
        buildExportRequestBody(spreadsheetData, slicerSelections, format)
      )
      .then(() => true)
      .catch((error) => {
        displayErrorMessage(error)
        return false
      })
    setDownloadingFormat(null)
    // A failed export leaves the menu open, to try again.
    if (ready) close()
  }

  const handleClickDownloadZip = async () => {
    if (busy || !spreadsheetData) return
    setDownloadingZip(true)
    const ready = await exportAttachmentsAsZip(spreadsheetData, setZipProgress, displayErrorMessage)
    setDownloadingZip(false)
    if (ready) close()
  }

  // As before, the ZIP waits until a view's unsaved changes (outside a document) are saved.
  const unsaved = !spreadsheetData.processId && spreadsheetData.userConfiguration.unsavedChanges
  const zipReason = () => {
    if (unsaved) return 'Save your changes to the view before you download its attachments'
    if (!canDownloadAttachments) return 'None of the rows shown have attachments'
    return undefined
  }

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width} zIndex={3000}>
      <MenuList>
        <MenuLabel>Export</MenuLabel>
        {/* Named as a group, so a screen reader hears "Export" with each short row: "CSV", "Excel". */}
        <div role="group" aria-label="Export" className="flex flex-col">
          <MenuItem
            data-tour="export-csv"
            data-cy={spreadsheetData.viewDetails.type === ViewTypes.SPREADSHEET ? 'download-csv' : 'download-png'}
            icon={downloadingFormat === 'csv' ? <MenuIconSpinner /> : <Csv />}
            disabled={busy && downloadingFormat !== 'csv'}
            onClick={() => handleExport('csv')}
          >
            CSV
          </MenuItem>
          <MenuItem
            data-tour="export-excel"
            data-cy="download-xlsx"
            icon={downloadingFormat === 'xlsx' ? <MenuIconSpinner /> : <Excel />}
            disabled={busy && downloadingFormat !== 'xlsx'}
            onClick={() => handleExport('xlsx')}
          >
            Excel
          </MenuItem>
          {hasAttachmentColumns && (
            <MenuItem
              data-cy="export-attachments"
              icon={downloadingZip ? <MenuIconSpinner /> : <Zip />}
              disabled={busy && !downloadingZip}
              disabledReason={zipReason()}
              onClick={handleClickDownloadZip}
            >
              {downloadingZip ? `Downloading ${zipProgress.done} of ${zipProgress.total}` : 'Attachments as ZIP'}
            </MenuItem>
          )}
        </div>
      </MenuList>
    </Menu>
  )
}

export default ExportMenu
